/**
 * The face a food wears — see the nutrition spec, Logging an entry. Nothing chooses one: a
 * food's face is read off its name, and a meal's or a recipe's is a deck of faces, read off its
 * name and what it is made of.
 *
 * Every app reads the same table, `table/food-marks.json`: a list of faces, each an emoji and,
 * where one has been drawn, an icon, with the words in each language that call for it. A name
 * wears the face of the first entry any of whose words it holds at the start of a word, so a word
 * that carries another
 * stands before it — cottage cheese before cheese, pineapple before apple — and the package's
 * own test fails when an entry has been put where one of its words can never be reached.
 *
 * The emoji is never left out, even beside an icon: a face said in text, as in a shared food,
 * is said with the emoji, and an icon is only a better picture of the same thing.
 */

import table from '../table/food-marks.json'

/** What a food wears: an emoji always, and the icon that draws it better where there is one. */
export interface Face {
  readonly emoji: string
  readonly icon: string | null
}

/** One entry of the table: a face, and the words in each language that call for it. */
export interface Mark {
  readonly emoji: string
  readonly icon?: string
  readonly words: Readonly<Record<string, readonly string[]>>
}

export const MARKS: readonly Mark[] = table.marks

/** What a food with no face of its own and a name the table says nothing about wears. */
export const PLAIN_FACE: Face = { emoji: '🍽️', icon: null }

/**
 * A letter read as another for the lookup: a Hebrew final letter as its plain form, so a
 * keyword written in the singular is found in the plural, and the Hebrew quote marks as the
 * plain ones, so צ׳יפס and צ'יפס are one word.
 */
const FOLDED_LETTERS: Readonly<Record<string, string>> = {
  ך: 'כ',
  ם: 'מ',
  ן: 'נ',
  ף: 'פ',
  ץ: 'צ',
  '״': '"',
  '׳': "'",
}

/**
 * A name as the lookup reads it: lower-cased, with every letter of `FOLDED_LETTERS` folded, and a
 * hyphen read as the space it stands for, so "Egg-white omelette" holds egg white.
 */
export function fold(name: string): string {
  return name
    .toLowerCase()
    .replace(/[ךםןףץ״׳-]/g, (letter) => (letter === '-' ? ' ' : (FOLDED_LETTERS[letter] ?? letter)))
}

/**
 * A word as the lookup holds it: folded, and whether it has to end where the name's word ends —
 * written with a `$` after it in the table, for a short word that starts longer ones it has
 * nothing to do with: pie and pieces, bar and barley.
 */
interface Word {
  readonly text: string
  readonly whole: boolean
}

/** A word of the table as the lookup holds it. */
export function wordOf(written: string): Word {
  const whole = written.endsWith('$')
  return { text: fold(whole ? written.slice(0, -1) : written), whole }
}

const FOLDED: readonly { readonly face: Face; readonly words: readonly Word[] }[] = MARKS.map(
  (mark) => ({
    face: { emoji: mark.emoji, icon: mark.icon ?? null },
    words: Object.values(mark.words).flat().map(wordOf),
  }),
)

/**
 * A letter that stands in front of a Hebrew word as part of it — and, in, the, to, from, that,
 * as — so that a name holding חומוס as וחומוס still wears it.
 */
const HEBREW_PREFIXES = 'ובהלמשכ'

/** Whether a letter or a digit, which a word cannot start straight after. */
function inWord(letter: string | undefined): boolean {
  // A decimal digit only, as every port reads one: ½ is no digit a word runs on from.
  return letter !== undefined && /[\p{L}\p{Nd}]/u.test(letter)
}

/**
 * Whether a folded name holds a word from its start: at the start of the name, after anything
 * that is not a letter or a digit, or after a Hebrew prefix standing at the start of a word.
 * It may run on past the word's end, so a word written in the singular, or as a stem, is found
 * in the plural too, unless the word is written whole — but never from inside another word, which is how quesadilla came to wear
 * dill and Classic kefir.
 */
function holds(folded: string, { text, whole }: Word): boolean {
  for (let at = folded.indexOf(text); at !== -1; at = folded.indexOf(text, at + 1)) {
    if (whole && inWord(folded[at + text.length])) continue
    const before = folded[at - 1]
    if (!inWord(before)) return true
    if (before !== undefined && HEBREW_PREFIXES.includes(before) && !inWord(folded[at - 2])) {
      return true
    }
  }
  return false
}

/** The face a name calls for. */
export function faceForName(name: string): Face {
  const folded = fold(name)
  return FOLDED.find(({ words }) => words.some((word) => holds(folded, word)))?.face ?? PLAIN_FACE
}

/**
 * Whether two faces are one picture: the icon where one is drawn, else the emoji — two faces
 * that share an icon, cream cheese and margarine, look the same whatever their emoji.
 */
function same(a: Face, b: Face): boolean {
  return (a.icon ?? a.emoji) === (b.icon ?? b.emoji)
}

/** One of the things a meal or a recipe is made of, as its deck reads it. */
export interface Part {
  readonly name: string
  readonly calories: number
}

/** How many faces a deck holds at most. */
export const DECK_SIZE = 3

/**
 * The faces a meal or a recipe wears, the one in front first: the face its name calls for,
 * where it calls for one, then those of what it is made of, the most calories first, each
 * picture once and none of them the plate — at most [DECK_SIZE]. Something that says nothing
 * by its name and holds nothing that does wears the plate alone.
 */
export function deckOf(name: string, parts: readonly Part[]): readonly Face[] {
  const deck: Face[] = []
  const add = (face: Face) => {
    if (deck.length < DECK_SIZE && !same(face, PLAIN_FACE) && !deck.some((it) => same(it, face))) {
      deck.push(face)
    }
  }
  add(faceForName(name))
  const byCalories = parts
    .map((part, index) => ({ part, index }))
    .sort((a, b) => b.part.calories - a.part.calories || a.index - b.index)
  for (const { part } of byCalories) add(faceForName(part.name))
  return deck.length === 0 ? [PLAIN_FACE] : deck
}
